/**
 * Alerts panel page (route `/alerts`).
 *
 * Spec §10.4 + §14 #3 — the BG / Warranty Watch surface in Cross-project
 * Visibility. Two sections:
 *
 *   1. BG-release-overdue (stronger surface)
 *      Projects whose Warranty End is in the past AND have an ACCEPTED BG
 *      that's not yet released. The matching notification type fires from
 *      the cron scanner; this page is the always-current snapshot.
 *
 *   2. Warranty End approaching
 *      Projects whose Warranty End is within the next 30 days — operator
 *      should plan the BG release.
 *
 * Both lists are sorted most-urgent-first (overdue: longest past first;
 * approaching: soonest first).
 *
 * Visible to both roles (the operator surface, not Admin-only).
 */
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ChevronRight, Clock, ShieldCheck } from 'lucide-react';
import { formatCurrency, formatDate } from '@procurement/shared';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { PageHeader } from '@/components/ui/page-header';
import { Spinner } from '@/components/ui/spinner';
import { useAlerts, type AlertProject } from '@/lib/visibility-queries';
import { usePageTitle } from '@/lib/use-page-title';

export function AlertsPage() {
  usePageTitle('Alerts');
  const query = useAlerts();

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Alerts" />
        <div className="flex h-full items-center justify-center py-16">
          <Spinner className="h-6 w-6 text-secondary" />
        </div>
      </>
    );
  }
  // Patch 5 — surface API failures explicitly. Without this the page would
  // render two "no items" sections when the query 500s, indistinguishable
  // from a healthy "nothing to alert" state.
  if (query.isError || !query.data) {
    return (
      <>
        <PageHeader title="Alerts" />
        <ErrorPanel
          title="Could not load alerts"
          onRetry={() => query.refetch()}
        />
      </>
    );
  }

  const overdue = query.data?.bgReleaseOverdue ?? [];
  const ndcPending = query.data?.ndcPending ?? [];
  const approaching = query.data?.warrantyApproaching ?? [];

  return (
    <>
      <PageHeader
        title="Alerts"
        subtitle="BG and warranty watch surface — projects needing post-delivery follow-up. Spec §10.4."
      />

      {/* Overdue section */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-status-danger" strokeWidth={1.5} />
            <span>BG release overdue</span>
            <Badge tone="danger">{overdue.length}</Badge>
          </div>
        }
        bodyClassName="p-0"
        className="mb-6"
      >
        {overdue.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" />}
            title="Nothing overdue"
            body="No project currently has a past-warranty BG awaiting release."
          />
        ) : (
          <ul className="divide-y divide-border-default">
            {overdue.map((p) => (
              <AlertRow key={p.projectId} project={p} kind="overdue" />
            ))}
          </ul>
        )}
      </Card>


      {/* NDC pending section */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-status-warning" strokeWidth={1.5} />
            <span>NDC pending after warranty expiry</span>
            <Badge tone="warning">{ndcPending.length}</Badge>
          </div>
        }
        bodyClassName="p-0"
        className="mb-6"
      >
        {ndcPending.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" />}
            title="No NDC pending"
            body="No open project has crossed warranty expiry without NDC issuance."
          />
        ) : (
          <ul className="divide-y divide-border-default">
            {ndcPending.map((p) => (
              <AlertRow key={p.projectId} project={p} kind="ndc" />
            ))}
          </ul>
        )}
      </Card>

      {/* Approaching section */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-status-warning" strokeWidth={1.5} />
            <span>Warranty ending in the next 30 days</span>
            <Badge tone="warning">{approaching.length}</Badge>
          </div>
        }
        bodyClassName="p-0"
      >
        {approaching.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" />}
            title="No upcoming BG releases"
            body="No project's warranty end falls within the next 30 days."
          />
        ) : (
          <ul className="divide-y divide-border-default">
            {approaching.map((p) => (
              <AlertRow key={p.projectId} project={p} kind="approaching" />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function AlertRow({
  project,
  kind,
}: {
  project: AlertProject;
  kind: 'overdue' | 'approaching' | 'ndc';
}) {
  // For overdue: days are negative — show as "N days ago".
  // For approaching: days >= 0 — show as "in N days" / "today".
  const daysLabel =
    kind === 'overdue' || kind === 'ndc'
      ? `${Math.abs(project.daysToWarrantyEnd)} day${Math.abs(project.daysToWarrantyEnd) === 1 ? '' : 's'} past warranty`
      : project.daysToWarrantyEnd === 0
        ? 'today'
        : `in ${project.daysToWarrantyEnd} day${project.daysToWarrantyEnd === 1 ? '' : 's'}`;

  return (
    <li>
      <Link
        to="/projects/$projectId"
        params={{ projectId: project.projectId }}
        className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-subtle"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-primary">
              {project.projectNumber}
            </span>
            <span className="truncate text-sm text-secondary">{project.projectTitle}</span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-secondary">
            <span>Warranty end: {formatDate(project.warrantyEndDate)}</span>
            <span>·</span>
            <span>{daysLabel}</span>
            {project.activeBgAmountPaisa !== null ? (
              <>
                <span>·</span>
                <span>BG {formatCurrency(project.activeBgAmountPaisa)}</span>
              </>
            ) : null}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-tertiary" strokeWidth={1.5} />
      </Link>
    </li>
  );
}
